import React from 'react';
import { SearchField } from '@lifeomic/chroma-react/components/SearchField';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';

const messages = defineMessages({
  keyFindings: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.keyFindings',
    defaultMessage: 'Key Findings',
  },
  search: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.search',
    defaultMessage: 'Search or type to add…',
  },
  searchKeyFindings: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.searchKeyFindings',
    defaultMessage: 'Search key findings',
  },
  deleteKeyFinding: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.deleteKeyFinding',
    defaultMessage: 'Delete key finding: {keyFinding}',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  keyFindings: {
    overflow: 'hidden',
  },
  chips: {
    backgroundColor: theme.hexToRgba(theme.palette.graphite[900], 0.025),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1),
    overflowY: 'auto',
  },
  chip: {
    minWidth: 0,
    '& > span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

export const KeyFindingsEditor = ({
  onSelect,
  onDelete,
  findings,
  disabled,
}: {
  onSelect: (keyFinding: string) => void;
  onDelete: (keyFinding: string) => void;
  findings: string[];
  disabled?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const [searchTerm, setSearchTerm] = React.useState<string>('');

  return (
    <Box className={classes.keyFindings} direction="column" gap={1}>
      <Box direction="column" gap={1}>
        <Text size="subbody" weight="bold">
          {formatMessage(messages.keyFindings)}
        </Text>
        <SearchField
          onSearch={term => {
            // onSearch fires when the user hits enter
            if (term && typeof term === 'string') {
              onSelect(term);
            }
            setSearchTerm('');
          }}
          onChange={e => setSearchTerm(e.target.value || '')}
          aria-label={formatMessage(messages.searchKeyFindings)}
          placeholder={formatMessage(messages.search)}
          fullWidth
          disabled={disabled}
        />
      </Box>
      <Box className={classes.chips} gap={1} flexWrap>
        {findings.map((keyFinding, idx) => (
          <React.Fragment key={idx}>
            {keyFinding.length > 35 ? (
              <Tooltip title={keyFinding}>
                <Chip
                  className={classes.chip}
                  style={{
                    opacity:
                      !searchTerm ||
                      keyFinding
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                        ? 1
                        : 0.5,
                  }}
                  onDelete={() => onDelete(keyFinding)}
                  label={keyFinding}
                  disableDelete={disabled}
                />
              </Tooltip>
            ) : (
              <Chip
                className={classes.chip}
                style={{
                  opacity:
                    !searchTerm ||
                    keyFinding.toLowerCase().includes(searchTerm.toLowerCase())
                      ? 1
                      : 0.5,
                }}
                onDelete={() => onDelete(keyFinding)}
                label={keyFinding}
                disableDelete={disabled}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
